import React from "react";
import { Modal, ModalHeader, ModalBody, Table } from "reactstrap";
import { fDMA } from "../../../utils/fecha";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(
    Number(n) || 0
  );
const fmtN = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(n) || 0);
const fecha = (f) => (f ? fDMA(String(f).slice(0, 10)) : "");

// Cómo se compone el costo de un lote (costo aterrizado del embarque)
function ComposicionLote({ d, costo }) {
  if (!d) return null;
  if (d.inicial) return <span>Inventario inicial (costo capturado en pesos)</span>;
  return (
    <span>
      FOB {fmt(d.fobUSD)} USD × TC {fmtN(d.tipoCambio)} = {fmt(d.fobPesos)}
      {Number(d.comisionPesos) > 0 ? <> + comisión {fmt(d.comisionPesos)}</> : null}
      {" "}+ gastos {fmt(d.gastoPorPieza)}
      {Number(d.impuestosPorPieza) > 0 ? <> + impuestos {fmt(d.impuestosPorPieza)}</> : null}
      {" "}= <b>{fmt(costo)}</b>
    </span>
  );
}

/**
 * De dónde sale el costo de una venta. El costo de cada artículo es un
 * promedio que se mueve cada vez que entra un embarque:
 *   - Al 31-ago-2026 arrancó con el costo de su último lote recibido.
 *   - Cada lote que entra después se promedia contra la existencia de ese día.
 *   - Cada venta se lleva el costo vigente el día que se surtió.
 *
 * Props: historial (GET articulosEmbarques/historialCosto/:articulo), venta
 * (renglón del desglose, opcional), isOpen, toggle, loading.
 */
export default function HistorialCostoModal({ isOpen, toggle, historial, venta, loading }) {
  const h = historial;
  const fv = venta && !venta.pendiente ? String(venta.fecha || "").slice(0, 10) : "";

  // Qué costo le toca a la venta según el historial
  let aplica = null; // "base" | índice de la entrada | "actual"
  let esperado = null;
  if (h) {
    if (venta?.pendiente) {
      aplica = "actual";
      esperado = h.articulo?.costoMX;
    } else if (fv && fv <= h.fechaArranque) {
      aplica = "base";
      esperado = h.base?.costo ?? null;
    } else if (fv) {
      const previas = (h.entradas || []).map((e, i) => ({ e, i })).filter(({ e }) => e.fecha <= fv);
      if (previas.length) {
        aplica = previas[previas.length - 1].i;
        esperado = previas[previas.length - 1].e.costoDespues;
      } else {
        aplica = "base";
        esperado = h.base?.costo ?? null;
      }
    }
  }
  const difiere =
    venta && esperado != null && Math.abs(Number(venta.costoUnitario || 0) - Number(esperado)) >= 0.01;
  const resalta = { backgroundColor: "#fff3cd", fontWeight: 600 };

  return (
    <Modal size="xl" isOpen={isOpen} toggle={toggle} style={{ maxWidth: "92%" }}>
      <ModalHeader toggle={toggle}>
        <h4>
          ¿De dónde sale el costo? — {h?.articulo?.codigo} {h?.articulo?.nombre}
          {venta ? (
            <small style={{ display: "block", fontSize: 13, color: "#6c757d", marginTop: 4 }}>
              {venta.pendiente
                ? `Pedido ${venta.pedido} pendiente de surtir: se valúa al costo actual del artículo`
                : `Venta del ${fecha(venta.fecha)} · Nota ${venta.surtido} · costo ${fmt(venta.costoUnitario)} por pieza`}
            </small>
          ) : null}
        </h4>
      </ModalHeader>
      <ModalBody>
        {loading || !h ? (
          <div align="center">Cargando…</div>
        ) : (
          <>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>
              El costo de un artículo es un <b>promedio</b> que se mueve cada vez que entra un embarque. El{" "}
              <b>{fecha(h.fechaArranque)}</b> arrancó con el costo de su último lote recibido; cada lote que entra
              después se promedia contra la existencia que había ese día:{" "}
              <i>(existencia × costo vigente + piezas del lote × costo del lote) ÷ (existencia + piezas)</i>. Cada venta se
              lleva el costo vigente el día que se surtió. El costo de cada lote es FOB × TC + gastos del embarque
              (prorrateados por FOB) + impuestos.
            </div>

            {difiere ? (
              <div
                style={{ background: "#f8d7da", color: "#842029", padding: "8px 12px", borderRadius: 6, marginBottom: 12, fontSize: 13 }}
              >
                El costo grabado en esta venta ({fmt(venta.costoUnitario)}) no es el que da el historial (
                {fmt(esperado)}). Pasa cuando se corrigen gastos, precios o TC de un embarque y todavía no se recostea.
              </div>
            ) : null}

            <h6 style={{ marginTop: 6 }}>1. Arranque ({fecha(h.fechaArranque)}): último lote recibido</h6>
            {h.lotesPrevios?.length ? (
              <Table size="sm" bordered className="table-responsive-xl">
                <thead>
                  <tr style={{ fontSize: 12 }}>
                    <th>Recibido</th>
                    <th>Embarque</th>
                    <th align="right">Piezas</th>
                    <th>Costo del lote</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: 13 }}>
                  {h.lotesPrevios.map((l, i) => {
                    const esBase = i === h.lotesPrevios.length - 1;
                    return (
                      <tr key={i} style={esBase && aplica === "base" ? resalta : undefined}>
                        <td>{fecha(l.fecha)}</td>
                        <td>
                          #{l.embarque} {l.contenedor}
                          {esBase ? <b> ← costo de arranque</b> : null}
                        </td>
                        <td align="right">{fmtN(l.cantidad)}</td>
                        <td>
                          <ComposicionLote d={l.desglose} costo={l.costo} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              <p style={{ fontSize: 13 }}>No había lotes con costo antes del arranque.</p>
            )}

            <h6 style={{ marginTop: 14 }}>2. Embarques que entraron después</h6>
            {h.entradas?.length ? (
              <Table size="sm" bordered className="table-responsive-xl">
                <thead>
                  <tr style={{ fontSize: 12 }}>
                    <th>Recibido</th>
                    <th>Embarque</th>
                    <th>Costo del lote</th>
                    <th>Promedio</th>
                    <th align="right">Costo vigente</th>
                  </tr>
                </thead>
                <tbody style={{ fontSize: 13 }}>
                  {h.entradas.map((e, i) => (
                    <tr key={i} style={aplica === i ? resalta : undefined}>
                      <td>{fecha(e.fecha)}</td>
                      <td>
                        #{e.embarque} {e.contenedor}
                      </td>
                      <td>
                        <ComposicionLote d={e.desglose} costo={e.costoLote} />
                      </td>
                      <td>
                        {Number(e.costoAntes) > 0 && e.existenciaAntes > 0 ? (
                          <>
                            ({fmtN(e.existenciaAntes)} pzas × {fmt(e.costoAntes)} + {fmtN(e.cantidad)} pzas ×{" "}
                            {fmt(e.costoLote)}) ÷ {fmtN(e.existenciaAntes + e.cantidad)}
                          </>
                        ) : (
                          <>Sin existencia con costo: toma el costo del lote ({fmtN(e.cantidad)} pzas)</>
                        )}
                        {e.existenciaNegativa ? (
                          <div style={{ color: "#b8860b", fontSize: 12 }}>
                            Al historial le faltan movimientos (la existencia calculada salía negativa)
                          </div>
                        ) : null}
                      </td>
                      <td align="right">
                        <b>{fmt(e.costoDespues)}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <p style={{ fontSize: 13 }}>No ha entrado ningún embarque después del arranque.</p>
            )}

            <div style={{ fontSize: 14, marginTop: 8 }}>
              <span style={aplica === "actual" ? resalta : undefined}>
                Costo actual del artículo: <b>{fmt(h.articulo?.costoMX)}</b>
              </span>
              {" · "}Existencia hoy: {fmtN(h.existenciaHoy)} pzas
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  );
}
